// src/config/censorship.ts
// Censorship flags for the "clean" launch. NEW roasts default to the cleanest combo —
// bleepMode "soundalike" (spoken audio is clean minced-oaths) + captionMask "none" (the
// already-clean stand-in shows in full). The StudioCreate Selects let you change either.
// Already-saved LEGACY roasts (generated before these fields existed) are read back with
// LEGACY_CAPTION_MASK/LEGACY_BLEEP_MODE = today's behavior, so their real-curse audio stays
// masked/bleeped exactly as before rather than being retroactively uncensored.
//
//  captionMask — how the caption layer redacts a profane word:
//    "vowels"    → today: star every vowel        (shit → sh*t, fuck → f*ck)
//    "keepFirst" → keep the first N letters, star the rest (shit → sh**, fuck → fu**)
//    "box"       → hide the word behind a solid redacted box (render change, not text)
//    "none"      → no caption redaction — show the full word (audio bleep is separate; see bleepMode)
//
//  bleepMode — whether the SPOKEN audio is real profanity or a clean sound-alike:
//    "tone"      → today: the real word is spoken, then bleeped (1 kHz tone + duck)
//    "soundalike"→ a clean minced-oath is spoken instead, then bleeped on top — so
//                  nothing offensive is ever actually said or spelled. The swap is a
//                  pre-TTS word substitution (see runGeneration.ts / deckPipeline.ts);
//                  the player then bleeps the swapped tokens via the shared profanity
//                  dictionary exactly as it bleeps real curses.

export type CaptionMask = "vowels" | "keepFirst" | "box" | "none";
export type BleepMode = "tone" | "soundalike";

// Defaults for NEW roasts (Select initial values + the stamp when a request omits them).
export const DEFAULT_CAPTION_MASK: CaptionMask = "none";        // show the (clean) word in full
export const DEFAULT_BLEEP_MODE: BleepMode = "soundalike";      // clean minced-oath audio

// Fallbacks when READING a legacy saved roast that predates these fields — == today's
// behavior, so old real-curse roasts keep their masked/bleeped display on replay.
export const LEGACY_CAPTION_MASK: CaptionMask = "vowels";
export const LEGACY_BLEEP_MODE: BleepMode = "tone";

/** How many leading letters keepFirst preserves before starring the rest (shit → sh**). */
export const KEEP_FIRST_LETTERS = 2;

/** UI option lists (default leads each list) — used by the opt-in StudioCreate Selects. */
export const CAPTION_MASK_OPTIONS: CaptionMask[] = ["vowels", "keepFirst", "box", "none"];
export const BLEEP_MODE_OPTIONS: BleepMode[] = ["tone", "soundalike"];

/** Human labels for the Selects. */
export const CAPTION_MASK_LABELS: Record<CaptionMask, string> = {
  vowels: "Stars (sh*t)",
  keepFirst: "Keep-first (sh**)",
  box: "Redacted box",
  none: "None (full word)",
};
export const BLEEP_MODE_LABELS: Record<BleepMode, string> = {
  tone: "Bleep tone",
  soundalike: "Sound-alike (clean)",
};
